import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType, type UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";

import type {OttopilotDataViewerConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import type {WorkspaceMobileNavigationConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {WorkspaceMobileBottomCreateItem} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {OttopilotAdditionalButtonKind, OttopilotAdditionalButtonFormFieldPresentation, OttopilotAdditionalButtonFormFieldRole, OttopilotOperator, OttopilotRunTimeValueKind, type OttopilotAdditionalButtonVisibilityRule, type OttopilotAdditionalButtonsConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {OttopilotDispositionEmitWhen,
OttopilotDispositionOptionRuleActionKind,
OttopilotDispositionOptionRuleCountMode,
OttopilotDispositionPluginKind,
OttopilotSuggestedJourneyStatusRuleKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotDispositionConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotTriggerConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import {OttopilotScheduleValueEncoding,
OttopilotTrigger,
OttopilotTriggerDataDriver} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import {type AonWorkspaceConfig, computeAonDaysFromCreatedAtIso} from "~/utilities/syntheticFields/aon";
import type {EventWithEventContainer, EventContainerWithEvents} from "~/utilities/apis/api";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {
    getEventDispositionId,
    getEventContainerDispositionId,
} from "~/utilities/ottopilotSemanticColumns";
import {
    EMOTORAD__CUSTOMERS__ARCHIVE_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__ASSIGNED_DEALERS_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CALLS_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CHURNED_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CONVERTED_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_NAME_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_PLATFORM_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID,
    EMOTORAD__CUSTOMERS__DUPLICATE_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LAST_CUSTOMER_ACTIVITY_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LAST_EVENT_CONTAINER_CREATED_BY_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LAST_EVENT_CONTAINER_CREATED_ON_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LEADS_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LIVE_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__PHONE_NUMBER_COLUMN_ID,
    EMOTORAD__CUSTOMERS__SALES_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__SOURCE_CHANNEL_COLUMN_ID,
    EMOTORAD__CUSTOMERS__TEST_RIDES_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__WALKINS_COUNT_COLUMN_ID,
    EmotoradCustomerStatusEnum,
    emotoradCustomerStatuses,
    getEmotoradCustomerStatuses,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/Customers";
import {
    EMOTORAD__CALL__EDIT__FORM__ID,
    EMOTORAD__CALL__LOG__FORM__ID,
    EMOTORAD__CALL__SCHEDULE__FORM__ID,
    EMOTORAD__EVENT__CALL__FBCLID__COLUMN_ID,
    EMOTORAD__EVENT__CALL__GCLID__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_DURATION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_END_TIME__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_START_TIME__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODIFIED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_VEHICLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_HOUSE_NO__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_LANDMARK__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__FIRST_CYCLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__OTHER_BRANDS_CONSIDERED__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_OWNER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_OWNER_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ALTERNATE_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__FRAME_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__FRANCHISE_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__KAM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__LAYOUT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ORIGIN_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__RM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_MAIL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_ZONE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__MODIFIED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LAST_WALKIN_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__MODIFIED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_TERM__COLUMN_ID,
    EMOTORAD__SALES__EDIT__FORM__ID,
    EMOTORAD__SALES__LOG__FORM__ID,
    EMOTORAD__SALES__SCHEDULE__FORM__ID,
    EMOTORAD__TESTRIDE__EDIT__FORM__ID,
    EMOTORAD__TESTRIDE__LOG__FORM__ID,
    EMOTORAD__TESTRIDE__SCHEDULE__FORM__ID,
    EMOTORAD__WALKIN__EDIT__FORM__ID,
    EMOTORAD__WALKIN__LOG__FORM__ID,
    EMOTORAD__WALKIN__SCHEDULE__FORM__ID,
    EmotoradCallStatusEnum,
    emotoradCallStatuses,
    EmotoradEventCallDispositionEnum,
    emotoradEventCallDispositions,
    EmotoradEventTypes,
    EmotoradInputSourceEnum,
    emotoradInputSources,
    EmotoradInputTypeEnum,
    emotoradInputTypes,
    EmotoradLeadEventStatusEnum,
    emotoradLeadEventStatuses,
    EmotoradSalesEventDispositionEnum,
    emotoradSalesEventDispositions,
    EmotoradSalesStatusEnum,
    emotoradSalesStatuses,
    EventCallType,
    getEmotoradCallStatuses,
    getEmotoradEventCallDispositions,
    getEmotoradInputSourceSelectOptions,
    getEmotoradInputTypeSelectOptions,
    getEmotoradLeadEventStatuses,
    getEmotoradSalesEventDispositions,
    getEmotoradSalesStatuses,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/Events";
import {
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__GCLID__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__REMARKS_RECENT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SALES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINERS__SCOPE_FILTER__ASSIGNED_TO_ME__OPTION_ID,
    EMOTORAD__EVENT_CONTAINERS__SCOPE_FILTER__CREATED_BY_ME__OPTION_ID,
    EMOTORAD_EVENT_CONTAINER_DISPOSITION_NEW_LEAD_L1_SLUG,
    EMOTORAD_EVENT_CONTAINER_DISPOSITION_RULES,
    emotoradCategories,
    EmotoradCategoryEnum,
    EmotoradCurrentPlatformEnum,
    emotoradCurrentPlatforms,
    EmotoradEnquiryDigitalLeadStatusEnum,
    emotoradEnquiryDigitalLeadStatuses,
    EmotoradEnquiryDispositionEnum,
    emotoradEnquiryDispositions,
    emotoradExpectedRidingFrequencies,
    EmotoradExpectedRidingFrequencyEnum,
    EmotoradExploringForEnum,
    emotoradExploringForOptions,
    EmotoradGenderEnum,
    emotoradGenders,
    EmotoradHeightOfRiderEnum,
    emotoradHeightOfRiderOptions,
    EmotoradEventContainerDispositionEnum,
    emotoradEventContainerDispositions,
    EmotoradEventContainerStatusEnum,
    emotoradEventContainerStatuses,
    EmotoradOtherBrandConsideredEnum,
    emotoradOtherBrandsConsidered,
    EmotoradPrimaryRidingPurposeEnum,
    emotoradPrimaryRidingPurposes,
    EmotoradPurchaseTimelineEnum,
    emotoradPurchaseTimelines,
    EmotoradSourceChannelEnum,
    emotoradSourceChannels,
    EmotoradYesNoEnum,
    emotoradYesNoOptions,
    getEmotoradCategorySelectOptions,
    getEmotoradCurrentPlatformSelectOptions,
    getEmotoradEnquiryDigitalLeadStatuses,
    getEmotoradEnquiryDispositions,
    getEmotoradExpectedRidingFrequencySelectOptions,
    getEmotoradExploringForSelectOptions,
    getEmotoradHeightOfRiderSelectOptions,
    getEmotoradEventContainerDispositionIdsRequiringScheduleNextCall,
    getEmotoradEventContainerDispositions,
    getEmotoradEventContainerStatuses,
    getEmotoradOtherBrandsConsideredSelectOptions,
    getEmotoradPrimaryRidingPurposeSelectOptions,
    getEmotoradPurchaseTimelineSelectOptions,
    getEmotoradSourceChannelSelectOptions,
    getEmotoradYesNoSelectOptions,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/EventContainers";
import type {
    EmotoradDynamicEventContainerSelectField,
    EmotoradEventContainerDispositionRule,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/EventContainers";
import {
    EMOTORAD__OU__AREA__NAME__COLUMN_ID,
    EMOTORAD__OU__COUNTRY__NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__AADHAR__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__ACTIVE_LEADS_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CONVERTED_LEADS_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__GST_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_EMAIL__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PAN__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PINCODE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__STATE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__TOTAL_LEADS_ASSIGNED__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__TOTAL_SSE_COUNT__COLUMN_ID,
    EMOTORAD__OU__STATE__NAME__COLUMN_ID,
    EMOTORAD__OU__ZONE__NAME__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_LAT__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_LONG__COLUMN_ID,
    EmotoradOus,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/OrganizationalUnits";
import {
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_HEX__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__DISCOUNTED_PRICE__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__MODEL_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__MODEL_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__PARENT_MODEL_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__PARENT_MODEL_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__PRICE_STARTING_FROM__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__SKU_ID__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__VARIANT_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__VARIANT_TEMPLATE_ID,
    EmotoradProductCatalogues,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/ProductCatalogue";
import {
    EMOTORAD__USER__AADHAR__COLUMN_ID,
    EMOTORAD__USER__ACTIVE_LIFECYCLE_COUNT__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_CITY__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_STATE__COLUMN_ID,
    EMOTORAD__USER__ALTERNATE_CONTACT__COLUMN_ID,
    EMOTORAD__USER__AREA__COLUMN_ID,
    EMOTORAD__USER__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID,
    EMOTORAD__USER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__USER__CONVERTED_LIFECYCLE_COUNT__COLUMN_ID,
    EMOTORAD__USER__COUNTRY__COLUMN_ID,
    EMOTORAD__USER__CREATED_BY__COLUMN_ID,
    EMOTORAD__USER__CREATED_ON__COLUMN_ID,
    EMOTORAD__USER__DEALERSHIP__COLUMN_ID,
    EMOTORAD__USER__DISPLAY_NAME__COLUMN_ID,
    EMOTORAD__USER__DOB__COLUMN_ID,
    EMOTORAD__USER__EMAIL__COLUMN_ID,
    EMOTORAD__USER__GENDER__COLUMN_ID,
    EMOTORAD__USER__GST_NUMBER__COLUMN_ID,
    EMOTORAD__USER__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__USER__LAST_ACTIVE_ON__COLUMN_ID,
    EMOTORAD__USER__NAME__COLUMN_ID,
    EMOTORAD__USER__PAN__COLUMN_ID,
    EMOTORAD__USER__PROFILE_IMAGE_URL__COLUMN_ID,
    EMOTORAD__USER__SALES_COUNT__COLUMN_ID,
    EMOTORAD__USER__STAFF_CODE__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__AADHAR__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ADDRESS_CITY__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ADDRESS_STATE__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ALTERNATE_CONTACT__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__PAN__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__STATUS__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__WHATSAPP_CONTACT__COLUMN_ID,
    EMOTORAD__USER__TEST_RIDE_COUNT__COLUMN_ID,
    EMOTORAD__USER__TOTAL_LEAD_COUNT__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_INVITED_AT__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_INVITED_BY__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_PROCESSED_BY__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_PROCESSED_ON__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_REJECT_REASON__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_SUBMITTED_ON__COLUMN_ID,
    EMOTORAD__USER__WALKIN_COUNT__COLUMN_ID,
    EMOTORAD__USER__WHATSAPP_CONTACT__COLUMN_ID,
    EmotoradRoles,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/Users";
import type {
    EmotoradUserTemplateColumnIds,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/Users";
import {
    EmotoradModuleNames,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/Workspace";

export {
    emotoradCustomerStatuses,
    getEmotoradCustomerStatuses,
    emotoradCallStatuses,
    emotoradSalesStatuses,
    emotoradSalesEventDispositions,
    emotoradEventCallDispositions,
    emotoradGenders,
    emotoradYesNoOptions,
    getEmotoradYesNoSelectOptions,
    emotoradCategories,
    getEmotoradCategorySelectOptions,
    emotoradPurchaseTimelines,
    getEmotoradPurchaseTimelineSelectOptions,
    emotoradSourceChannels,
    getEmotoradSourceChannelSelectOptions,
    emotoradCurrentPlatforms,
    getEmotoradCurrentPlatformSelectOptions,
    emotoradPrimaryRidingPurposes,
    getEmotoradPrimaryRidingPurposeSelectOptions,
    emotoradExploringForOptions,
    getEmotoradExploringForSelectOptions,
    emotoradExpectedRidingFrequencies,
    getEmotoradExpectedRidingFrequencySelectOptions,
    emotoradHeightOfRiderOptions,
    getEmotoradHeightOfRiderSelectOptions,
    emotoradOtherBrandsConsidered,
    getEmotoradOtherBrandsConsideredSelectOptions,
    emotoradInputTypes,
    getEmotoradInputTypeSelectOptions,
    emotoradInputSources,
    getEmotoradInputSourceSelectOptions,
    emotoradEventContainerDispositions,
    EMOTORAD_EVENT_CONTAINER_DISPOSITION_NEW_LEAD_L1_SLUG,
    EMOTORAD_EVENT_CONTAINER_DISPOSITION_RULES,
    getEmotoradEventContainerDispositionIdsRequiringScheduleNextCall,
    getEmotoradEventContainerDispositions,
    getEmotoradCallStatuses,
    getEmotoradSalesStatuses,
    getEmotoradSalesEventDispositions,
    getEmotoradEventCallDispositions,
    emotoradLeadEventStatuses,
    getEmotoradLeadEventStatuses,
    emotoradEnquiryDigitalLeadStatuses,
    getEmotoradEnquiryDigitalLeadStatuses,
    emotoradEnquiryDispositions,
    getEmotoradEnquiryDispositions,
};
export type {
    EmotoradUserTemplateColumnIds,
    EmotoradDynamicEventContainerSelectField,
    EmotoradEventContainerDispositionRule,
};
export {
    EmotoradOus,
    EmotoradRoles,
    EmotoradModuleNames,
    EmotoradEventTypes,
    EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__OU__COUNTRY__NAME__COLUMN_ID,
    EMOTORAD__OU__ZONE__NAME__COLUMN_ID,
    EMOTORAD__OU__STATE__NAME__COLUMN_ID,
    EMOTORAD__OU__AREA__NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__STATE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PINCODE__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_LAT__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_LONG__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_EMAIL__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__TOTAL_SSE_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__TOTAL_LEADS_ASSIGNED__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__ACTIVE_LEADS_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CONVERTED_LEADS_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__GST_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PAN__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__AADHAR__COLUMN_ID,
    EMOTORAD__CUSTOMERS__PHONE_NUMBER_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_NAME_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_PLATFORM_COLUMN_ID,
    EMOTORAD__CUSTOMERS__SOURCE_CHANNEL_COLUMN_ID,
    EMOTORAD__CUSTOMERS__ASSIGNED_DEALERS_COLUMN_ID,
    EMOTORAD__CUSTOMERS__EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LEADS_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LIVE_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CHURNED_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CONVERTED_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__DUPLICATE_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__ARCHIVE_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CALLS_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__WALKINS_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__TEST_RIDES_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__SALES_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LAST_CUSTOMER_ACTIVITY_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LAST_EVENT_CONTAINER_CREATED_ON_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LAST_EVENT_CONTAINER_CREATED_BY_COLUMN_ID,
    EmotoradCustomerStatusEnum,
    EMOTORAD__USER__NAME__COLUMN_ID,
    EMOTORAD__USER__DISPLAY_NAME__COLUMN_ID,
    EMOTORAD__USER__EMAIL__COLUMN_ID,
    EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__USER__DOB__COLUMN_ID,
    EMOTORAD__USER__GENDER__COLUMN_ID,
    EMOTORAD__USER__STAFF_CODE__COLUMN_ID,
    EMOTORAD__USER__PROFILE_IMAGE_URL__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_INVITED_AT__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_INVITED_BY__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_SUBMITTED_ON__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_PROCESSED_ON__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_PROCESSED_BY__COLUMN_ID,
    EMOTORAD__USER__VERIFICATION_REJECT_REASON__COLUMN_ID,
    EMOTORAD__USER__COUNTRY__COLUMN_ID,
    EMOTORAD__USER__AREA__COLUMN_ID,
    EMOTORAD__USER__DEALERSHIP__COLUMN_ID,
    EMOTORAD__USER__ALTERNATE_CONTACT__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ALTERNATE_CONTACT__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__WHATSAPP_CONTACT__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ADDRESS_STATE__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ADDRESS_CITY__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__AADHAR__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__PAN__COLUMN_ID,
    EMOTORAD__USER__WHATSAPP_CONTACT__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_STATE__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_CITY__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID,
    EMOTORAD__USER__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__USER__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__USER__GST_NUMBER__COLUMN_ID,
    EMOTORAD__USER__AADHAR__COLUMN_ID,
    EMOTORAD__USER__PAN__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__STATUS__COLUMN_ID,
    EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID,
    EMOTORAD__USER__LAST_ACTIVE_ON__COLUMN_ID,
    EMOTORAD__USER__ACTIVE_LIFECYCLE_COUNT__COLUMN_ID,
    EMOTORAD__USER__TOTAL_LEAD_COUNT__COLUMN_ID,
    EMOTORAD__USER__CONVERTED_LIFECYCLE_COUNT__COLUMN_ID,
    EMOTORAD__USER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__USER__WALKIN_COUNT__COLUMN_ID,
    EMOTORAD__USER__TEST_RIDE_COUNT__COLUMN_ID,
    EMOTORAD__USER__SALES_COUNT__COLUMN_ID,
    EMOTORAD__USER__CREATED_ON__COLUMN_ID,
    EMOTORAD__USER__CREATED_BY__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__PARENT_MODEL_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__MODEL_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__VARIANT_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__PARENT_MODEL_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__MODEL_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__VARIANT_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_HEX__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__PRICE_STARTING_FROM__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__DISCOUNTED_PRICE__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__SKU_ID__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__REMARKS_RECENT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__GCLID__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SALES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__CALL__SCHEDULE__FORM__ID,
    EMOTORAD__CALL__LOG__FORM__ID,
    EMOTORAD__CALL__EDIT__FORM__ID,
    EMOTORAD__WALKIN__SCHEDULE__FORM__ID,
    EMOTORAD__WALKIN__LOG__FORM__ID,
    EMOTORAD__WALKIN__EDIT__FORM__ID,
    EMOTORAD__TESTRIDE__SCHEDULE__FORM__ID,
    EMOTORAD__TESTRIDE__LOG__FORM__ID,
    EMOTORAD__TESTRIDE__EDIT__FORM__ID,
    EMOTORAD__SALES__SCHEDULE__FORM__ID,
    EMOTORAD__SALES__LOG__FORM__ID,
    EMOTORAD__SALES__EDIT__FORM__ID,
    EMOTORAD__EVENT__CALLS__CALL_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_START_TIME__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_END_TIME__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_DURATION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODIFIED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LAST_WALKIN_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__MODIFIED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__MODIFIED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__FRAME_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
    EMOTORAD__EVENT__SALES__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ORIGIN_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__FRANCHISE_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__LAYOUT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_ZONE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_OWNER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_OWNER_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ALTERNATE_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_MAIL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__KAM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__RM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_VEHICLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_HOUSE_NO__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_LANDMARK__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__FIRST_CYCLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__OTHER_BRANDS_CONSIDERED__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODIFIED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALL__FBCLID__COLUMN_ID,
    EMOTORAD__EVENT__CALL__GCLID__COLUMN_ID,
    EventCallType,
    EmotoradCallStatusEnum,
    EmotoradSalesStatusEnum,
    EmotoradProductCatalogues,
    EmotoradSalesEventDispositionEnum,
    EmotoradEventCallDispositionEnum,
    EmotoradGenderEnum,
    EmotoradYesNoEnum,
    EmotoradCategoryEnum,
    EmotoradPurchaseTimelineEnum,
    EmotoradSourceChannelEnum,
    EmotoradCurrentPlatformEnum,
    EmotoradPrimaryRidingPurposeEnum,
    EmotoradExploringForEnum,
    EmotoradExpectedRidingFrequencyEnum,
    EmotoradHeightOfRiderEnum,
    EmotoradOtherBrandConsideredEnum,
    EmotoradInputTypeEnum,
    EmotoradInputSourceEnum,
    EmotoradEventContainerDispositionEnum,
    EmotoradLeadEventStatusEnum,
    EMOTORAD__EVENT_CONTAINERS__SCOPE_FILTER__ASSIGNED_TO_ME__OPTION_ID,
    EMOTORAD__EVENT_CONTAINERS__SCOPE_FILTER__CREATED_BY_ME__OPTION_ID,
    EmotoradEnquiryDigitalLeadStatusEnum,
    EmotoradEnquiryDispositionEnum,
};

export {EmotoradEventContainerStatusEnum, emotoradEventContainerStatuses, getEmotoradEventContainerStatuses};

// Organizational Unit Template IDs — Country → State → Area → Dealership
// User Template IDs — reporting chain per emotoradSetup.md
/**
 * Emotorad manager roles (sidebar + direct URLs for Lifecycle Overview, In Queue, and Dealers).
 * Note: ZSM maps to the RM template in current setup.
 */
export const EMOTORAD_LIFECYCLE_OVERVIEW_NAV_VISIBLE_ROLE_TEMPLATE_IDS: ReadonlySet<string> = new Set<string>([
    EmotoradRoles.SystemAdmin,
    EmotoradRoles.SuperAdmin,
    EmotoradRoles.RM,
    EmotoradRoles.KAM,
]);

/** Same as Lifecycle Overview — dealership owner / store staff / SSE must not access Dealers. */
export const EMOTORAD_DEALERS_NAV_VISIBLE_ROLE_TEMPLATE_IDS: ReadonlySet<string> = EMOTORAD_LIFECYCLE_OVERVIEW_NAV_VISIBLE_ROLE_TEMPLATE_IDS;

/** Managers, dealership owner, and store manager; SSE excluded. */
export const EMOTORAD_MY_SALES_TEAM_NAV_VISIBLE_ROLE_TEMPLATE_IDS: ReadonlySet<string> = new Set<string>([
    ...EMOTORAD_LIFECYCLE_OVERVIEW_NAV_VISIBLE_ROLE_TEMPLATE_IDS,
    EmotoradRoles.DealershipOwner,
    EmotoradRoles.StoreManager,
]);

/** Dealership field roles — customer profile footer actions (Edit / Create / More). */
export const EMOTORAD_CUSTOMER_PROFILE_FOOTER_VISIBLE_ROLE_TEMPLATE_IDS: ReadonlySet<string> = new Set<string>([
    EmotoradRoles.DealershipOwner,
    EmotoradRoles.StoreManager,
    EmotoradRoles.StoreSalesExecutive,
]);

const EMOTORAD_EVENT_CREATE_FORM_URL_PREFIXES = ["emotorad/call/", "emotorad/walkin/", "emotorad/testride/", "emotorad/sale/"] as const;

export function getEmotoradListEventCreateItems(
    workspaceMobileNavigationConfiguration: WorkspaceMobileNavigationConfiguration | null,
): readonly WorkspaceMobileBottomCreateItem[] {
    const items = workspaceMobileNavigationConfiguration?.mobileBottomCreateSheet ?? [];
    return items.filter((item) => EMOTORAD_EVENT_CREATE_FORM_URL_PREFIXES.some((prefix) => item.formUrl.startsWith(prefix)));
}

export type EmotoradListModule = "journeysLifecycleOverview" | "journeysInQueue" | "events" | "dispositionTracker" | "customers" | "mySalesTeam";

export type EmotoradListModuleConfig = {
    enabled: boolean;
    showFooter: boolean;
    scheduleRoute: string;
    includeEventCreateDrawer: boolean;
};

export const EMOTORAD_LIST_MODULE_CONFIG: Readonly<Record<EmotoradListModule, EmotoradListModuleConfig>> = {
    journeysLifecycleOverview: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
    },
    journeysInQueue: {
        enabled: true,
        showFooter: false,
        scheduleRoute: "",
        includeEventCreateDrawer: false,
    },
    events: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
    },
    dispositionTracker: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
    },
    customers: {
        enabled: true,
        showFooter: false,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
    },
    mySalesTeam: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
    },
};

export function getEmotoradListModuleConfig(module: EmotoradListModule): EmotoradListModuleConfig {
    return EMOTORAD_LIST_MODULE_CONFIG[module];
}

// Module instance IDs (workspace configuration)
/** Select options for journey Last Activity — one option per event template (`EmotoradEventTypes`). */
export function getEmotoradEventTypeSelectOptions(): Array<{id: Uuid; displayName: string}> {
    return [
        {id: EmotoradEventTypes.call as Uuid, displayName: "Call"},
        {id: EmotoradEventTypes.lead as Uuid, displayName: "Lead"},
        {id: EmotoradEventTypes.walkin as Uuid, displayName: "Walk-in"},
        {id: EmotoradEventTypes.testride as Uuid, displayName: "Test ride"},
        {id: EmotoradEventTypes.sales as Uuid, displayName: "Sales"},
    ];
}

// Synthetic column ids for events
/** Must differ from {@link EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID}: table column `Map` keys by id and journey L1 would overwrite walk-in L1 otherwise. */
/** Test ride — synthetic L1/L2/L3 from journey-style disposition slug; must differ from journey L1–L3 column ids (table maps by column id). */
// Synthetic column ids for Disposition Tracker events
export const EMOTORAD__EVENT__DISPOSITION_TRACKER__L1_DISPOSITION__COLUMN_ID = "a1b2c3d4-e5f6-4a7b-8c9d-dt-l1-disp01" as Uuid;
export const EMOTORAD__EVENT__DISPOSITION_TRACKER__L2_DISPOSITION__COLUMN_ID = "a1b2c3d4-e5f6-4a7b-8c9d-dt-l2-disp02" as Uuid;
export const EMOTORAD__EVENT__DISPOSITION_TRACKER__L3_DISPOSITION__COLUMN_ID = "a1b2c3d4-e5f6-4a7b-8c9d-dt-l3-disp03" as Uuid;
// Synthetic journey column ids
export const EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID = "1874a3b8-e8fa-4b28-ad6a-3a5807d8e9b05" as Uuid;
export const EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID = "2306efbd-c2c7-4206-b978-cffd5e3b4e6e";

// Organizational Unit Column IDs
/** Dealership OU — derived from linked dealership-owner user and dealership-scoped journey/event counts. */

/** Dealership OU stored fields (editable on the OU row). */
export const EMOTORAD_DEALERSHIP_STORED_ADDITIONAL_COLUMN_IDS: Array<string> = [
    EMOTORAD__OU__DEALERSHIP__STATE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PINCODE__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_LAT__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_LONG__COLUMN_ID,
];

/** Dealership OU resolver-backed columns (`emotoradDealershipOuDerivedAdditionalColumns` in workspace config). */
export const EMOTORAD_DEALERSHIP_RESOLVER_COLUMN_IDS: Array<string> = [
    EMOTORAD__OU__DEALERSHIP__OWNER_NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_EMAIL__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__TOTAL_SSE_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__TOTAL_LEADS_ASSIGNED__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__ACTIVE_LEADS_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CONVERTED_LEADS_COUNT__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__GST_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PAN__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__AADHAR__COLUMN_ID,
];

/** Dealers table — stored + resolver columns (excludes Name; shown via dealer name column). */
export const EMOTORAD_DEALERSHIP_TABLE_ADDITIONAL_COLUMN_IDS: Array<string> = [
    ...EMOTORAD_DEALERSHIP_STORED_ADDITIONAL_COLUMN_IDS,
    ...EMOTORAD_DEALERSHIP_RESOLVER_COLUMN_IDS,
];

/** Dealers view panel — same order as table additional columns. */
export const EMOTORAD_DEALERSHIP_VIEW_PANEL_COLUMN_IDS: Array<string> = [...EMOTORAD_DEALERSHIP_TABLE_ADDITIONAL_COLUMN_IDS];

// User Column IDs
/** Multi-area scope (RM, KAM) — Area OU instances. */
/** Dealership scope (owner, store manager, sales executive). */
/** Dealership owner user template — alternate contact. */
/** Store manager & store sales executive templates — must not reuse dealership-owner column id. */
/** KYC / address fields on store staff templates (owner uses the non–store-staff ids below). */
/** Dealership owner (and shared semantics where store staff uses store-staff-specific ids). */
/** User-level analytics / SSE-specific columns. */
// `export const EMOTORAD__USER__SSE_CITY__COLUMN_ID = "c3d4e5f6-a7b8-9012-cdef-123456789012" as Uuid;
// export const EMOTORAD__USER__SSE_STATE__COLUMN_ID = "d4e5f6a7-b8c9-0123-def0-234567890123" as Uuid;`

// Product catalogue — hierarchical templates (Parent Model → Model → Variant → Color); IDs must match `-emotorad-workspace-config` productCatalogueTemplates
/** Legacy name used by workspace-creation scripts (`Variant Details` row in product catalogue templates). */
export const EMOTORAD__PRODUCT_CATALOGUE__DETAILS_TEMPLATE_ID = EMOTORAD__PRODUCT_CATALOGUE__COLOR_TEMPLATE_ID;

// Journey Column IDs — Emotorad credit-card leads (journeys = leads)

/** Resolves journey "Lifecycle owner" / "Lead owner" column id from workspace column definitions. */
export function resolveEmotoradJourneyLifecycleLeadOwnerColumnIdFromAdditionalColumns(
    additionalColumns: ReadonlyArray<{id: Uuid; displayName: string}>,
): Uuid | null {
    const normalized = (label: string): string => label.trim().toLowerCase();
    const lifecycleOwnerDisplayNames: ReadonlyArray<string> = ["lifecycle owner", "lead owner", "action owner"];
    const column = additionalColumns.find((item) => lifecycleOwnerDisplayNames.includes(normalized(item.displayName)));
    return column?.id ?? null;
}

/** Ordered lifecycle stages for list-card circular funnel (lead → sales). */
export type EmotoradJourneyListFunnelStageKey = "lead" | "call" | "walkin" | "testride" | "sales";

export type EmotoradJourneyListFunnelStage = {
    key: EmotoradJourneyListFunnelStageKey;
    columnId: Uuid;
};

export const EMOTORAD_JOURNEY_LIST_FUNNEL_STAGES: readonly EmotoradJourneyListFunnelStage[] = [
    {key: "lead", columnId: EMOTORAD__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID},
    {key: "call", columnId: EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID},
    {key: "walkin", columnId: EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID},
    {key: "testride", columnId: EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID},
    {key: "sales", columnId: EMOTORAD__EVENT_CONTAINER__SALES_COUNT__COLUMN_ID},
] as const;

export const EMOTORAD_JOURNEY_LIST_FUNNEL_STAGE_COLUMN_IDS: readonly Uuid[] = EMOTORAD_JOURNEY_LIST_FUNNEL_STAGES.map((stage) => stage.columnId);

// Call event form IDs

// Event (call) column IDs
// TODO: REMOVED THIS

/** Calls — emotoradSetup.csv §5. */
/** Call event — Follow up required (select). Options: {@link EmotoradYesNoEnum} / {@link getEmotoradYesNoSelectOptions}. */
/** Call event — Input type (select). Options: {@link EmotoradInputTypeEnum} / {@link getEmotoradInputTypeSelectOptions}. */
/** Call event — Input source (select). Options: {@link EmotoradInputSourceEnum} / {@link getEmotoradInputSourceSelectOptions}. */
/** Call event — Test ride taken (select). Options: {@link EmotoradYesNoEnum} / {@link getEmotoradYesNoSelectOptions}. */

// Walk-in event columns — emotoradSetup.csv §4 (flat disposition field, not L1/L2/L3).
/** Walk-in event — Follow up required (select). Options: {@link EmotoradYesNoEnum} / {@link getEmotoradYesNoSelectOptions}. */
/** Walk-in event — Input type (select). Options: {@link EmotoradInputTypeEnum} / {@link getEmotoradInputTypeSelectOptions}. */
/** Walk-in event — Input source (select). Options: {@link EmotoradInputSourceEnum} / {@link getEmotoradInputSourceSelectOptions}. */
/** Walk-in event — Test ride taken (select). Options: {@link EmotoradYesNoEnum} / {@link getEmotoradYesNoSelectOptions}. */

// Test ride event columns — emotoradSetup.csv §6.
// TODO:REMOVED

// Sales (retail) event columns — emotoradSetup.csv §7.

/** URL pattern for scheduling/logging calls (uses workspace call event template id). */
export const EMOTORAD__CALL_EVENT_CREATE_URL =
    `/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/${EmotoradEventTypes.call}/create` as const;

/** Lead events are created from digital ingestion only; URL kept for consistency with other workspaces. */
export const EMOTORAD__LEAD_EVENT_CREATE_URL =
    `/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/${EmotoradEventTypes.lead}/create` as const;

export const EMOTORAD__WALKIN_EVENT_CREATE_URL =
    `/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/${EmotoradEventTypes.walkin}/create` as const;

export const EMOTORAD__TESTRIDE_EVENT_CREATE_URL =
    `/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/${EmotoradEventTypes.testride}/create` as const;

export const EMOTORAD__SALES_EVENT_CREATE_URL =
    `/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/${EmotoradEventTypes.sales}/create` as const;

// Call status (event lifecycle)
export function isEmotoradWorkspace(workspace: {displayName: string}): boolean {
    const compact = workspace.displayName.toLowerCase().replace(/[^a-z0-9]/g, "");
    return compact.includes("emotorad")
        && compact.includes("ottopilot");
}

function emotoradPrimitiveToString(raw: unknown): string {
    if (typeof raw === "string") {
        return raw;
    }

    if (typeof raw === "number" || typeof raw === "boolean" || typeof raw === "bigint") {
        return String(raw);
    }

    if (raw instanceof Date) {
        return raw.toISOString();
    }

    return "";
}

/** When template metadata is missing, format stored values (e.g. Yes/No option uuids) for read-only display. */
export function formatEmotoradScalarForUnknownColumnDisplay(raw: unknown): string {
    if (raw === null || raw === undefined || raw === "") {
        return "-";
    }
    const asStr = emotoradPrimitiveToString(raw);
    if (asStr === "") {
        return "-";
    }
    const yesNo = Object.values(emotoradYesNoOptions).find((o) => o.id === asStr);
    if (yesNo !== undefined) {
        return yesNo.displayName;
    }
    return asStr;
}

/** Calendar titles often join customer name and contact with a space; prefer "Name, +91…". */
export function formatEmotoradCalendarEventTitle(title: string, mergedColumnValues: Record<Uuid, unknown>): string {
    const nameRaw = mergedColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID];
    const phoneRaw = mergedColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
    const name = emotoradPrimitiveToString(nameRaw).trim();
    const phone = emotoradPrimitiveToString(phoneRaw).trim();
    if (name === "" || phone === "") {
        return title;
    }
    const withoutComma = `${name} ${phone}`;
    const withComma = `${name}, ${phone}`;
    const normalized = title.trim().replace(/\s+/g, " ");
    if (normalized === withoutComma) {
        return withComma;
    }
    if (normalized.includes(withoutComma)) {
        return normalized.replace(withoutComma, withComma);
    }
    return title;
}

const emotoradCurrentPlatformDisplayNameById: Record<string, string> = Object.fromEntries(
    Object.values(emotoradCurrentPlatforms).map((platform) => [String(platform.id), platform.displayName]),
);

/** Resolves a current/action platform option UUID to its display name; falls back to the raw value. */
export function formatEmotoradCurrentPlatformCell(value: string): string {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "?" || trimmed === "-") {
        return "";
    }
    const resolved = emotoradCurrentPlatformDisplayNameById[trimmed];
    if (resolved !== undefined) {
        return resolved;
    }
    return trimmed;
}

/** Raw Current Platform on a call event (`EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID` only). */
export function getEmotoradCallEventCurrentPlatformRaw(eventAdditionalColumnValues: Record<Uuid, unknown>): string {
    const raw = eventAdditionalColumnValues[EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID];
    if (raw === null || raw === undefined) {
        return "";
    }
    return emotoradPrimitiveToString(raw).trim();
}

/** Display label for call event Current Platform; empty when unset. */
export function formatEmotoradCallEventCurrentPlatformForDisplay(raw: string): string {
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "?") {
        return "-";
    }
    const formatted = formatEmotoradCurrentPlatformCell(trimmed);
    if (formatted === "") {
        return "-";
    }
    return formatted;
}

/** Written to call log/schedule (and follow-up schedule) on submit — event column only. */
export const EMOTORAD_CALL_EVENT_SUBMIT_CURRENT_PLATFORM_VALUE = EmotoradCurrentPlatformEnum.OTTOPILOT as Uuid;

/** Values that must not appear in filter pickers (unknown placeholders, empty sentinels). */
export function isInvalidEmotoradFilterPickerValue(value: string): boolean {
    const trimmed = value.trim();
    return trimmed === "" || trimmed === "?" || trimmed === "-";
}

export function sanitizeEmotoradFilterColumnValues(values: Iterable<string>): Array<string> {
    return Array.from(values).filter((value) => isInvalidEmotoradFilterPickerValue(value) !== true);
}

export const EMOTORAD_ACTION_PLATFORM_COLUMN_IDS: ReadonlyArray<Uuid> = [
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
];

export function isEmotoradActionPlatformColumnId(columnId: string): boolean {
    return EMOTORAD_ACTION_PLATFORM_COLUMN_IDS.some((id) => String(id) === String(columnId));
}

const emotoradDynamicJourneySelectColumnIds: Record<EmotoradDynamicEventContainerSelectField, Uuid | null> = {
    exploringFor: null,
    ridingPurpose: null,
    ridingFrequency: null,
    riderHeight: null,
    isFirstCycle: null,
    otherBrand: null,
    purchaseTimeline: EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID,
    sourceChannel: EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID,
    category: EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID,
    currentPlatform: EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
};

const emotoradDynamicJourneySelectDisplayNameFallbacks: Record<EmotoradDynamicEventContainerSelectField, string[]> = {
    exploringFor: ["Exploring for"],
    ridingPurpose: ["Primary Riding Purpose", "Riding Purpose"],
    ridingFrequency: ["Expected Riding Frequency", "Riding Frequency"],
    riderHeight: ["Height of Rider", "Rider Height"],
    isFirstCycle: ["Is this your first cycle?", "Is this your first Cycle?"],
    otherBrand: ["Others Brands Considered", "Other brands Considered"],
    purchaseTimeline: ["Purchase Timeline"],
    sourceChannel: ["Source channel", "Source Channels"],
    category: ["Category"],
    currentPlatform: ["Action platform", "Current platform"],
};

const emotoradDynamicJourneySelectStaticFallbackOptions: Record<EmotoradDynamicEventContainerSelectField, Array<{id: Uuid; displayName: string}>> = {
    exploringFor: getEmotoradExploringForSelectOptions(),
    ridingPurpose: getEmotoradPrimaryRidingPurposeSelectOptions(),
    ridingFrequency: getEmotoradExpectedRidingFrequencySelectOptions(),
    riderHeight: getEmotoradHeightOfRiderSelectOptions(),
    isFirstCycle: getEmotoradYesNoSelectOptions(),
    otherBrand: getEmotoradOtherBrandsConsideredSelectOptions(),
    purchaseTimeline: getEmotoradPurchaseTimelineSelectOptions(),
    sourceChannel: getEmotoradSourceChannelSelectOptions(),
    category: getEmotoradCategorySelectOptions(),
    currentPlatform: getEmotoradCurrentPlatformSelectOptions(),
};

function normalizeEmotoradSelectName(value: string): string {
    return value.trim().toLowerCase().replaceAll(/\s+/g, " ");
}

export function getEmotoradDynamicJourneySelectOptions(params: {
    additionalColumns: ReadonlyArray<UdoColumn>;
    field: EmotoradDynamicEventContainerSelectField;
    userDefinedObjectOptionsByTemplateId?: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>;
}): Array<{id: Uuid; displayName: string}> {
    const columnId = emotoradDynamicJourneySelectColumnIds[params.field];
    const byId =
        columnId === null
            ? undefined
            : params.additionalColumns.find((column) => {
                  return (
                      column.type === UdoColumnType.userDefinedObject &&
                      column.id === columnId
                  );
              });
    const fallbackNames = new Set(emotoradDynamicJourneySelectDisplayNameFallbacks[params.field].map((name) => normalizeEmotoradSelectName(name)));
    const byDisplayName = params.additionalColumns.find((column) => {
        return (
            column.type === UdoColumnType.userDefinedObject &&
            fallbackNames.has(normalizeEmotoradSelectName(column.displayName))
        );
    });
    const matchedColumn = byId ?? byDisplayName;
    if (
        matchedColumn === undefined ||
        matchedColumn.type !== UdoColumnType.userDefinedObject
    ) {
        return emotoradDynamicJourneySelectStaticFallbackOptions[params.field];
    }
    return [...(params.userDefinedObjectOptionsByTemplateId?.get(
        matchedColumn.configuration.userDefinedObjectTemplateId,
    ) ?? emotoradDynamicJourneySelectStaticFallbackOptions[params.field])];
}

/** L1 group display label per `flatDisposition` first token — used by Disposition Analysis donut to bucket leaves into 5 groups. */
const EMOTORAD_DISPOSITION_L1_LABEL_BY_FLAT_KEY: Record<string, string> = {
    "new-lead": "New Lead",
    "not-connected": "Not Connected",
    connected: "Connected",
    interested: "Interested",
    "not-interested": "Not Interested",
};

/** Resolves a leaf disposition id → its L1 group label (e.g. `connected_invalid_spam` → "Connected"). */
export function getEmotoradDispositionL1Label(dispositionId: Uuid | string): string | null {
    const disposition = Object.values(emotoradEventContainerDispositions).find((d) => d.id === dispositionId);
    if (disposition === undefined) {
        return null;
    }
    const l1Key = splitEmotoradFlatDispositionIntoLevels(disposition.flatDisposition).l1;
    return EMOTORAD_DISPOSITION_L1_LABEL_BY_FLAT_KEY[l1Key] ?? disposition.displayName;
}

export function getEmotoradUniqueDispositionLevelValues(level: "l1" | "l2" | "l3"): Array<string> {
    const values = new Set<string>();
    for (const disposition of Object.values(emotoradEventContainerDispositions)) {
        const levels = splitEmotoradFlatDispositionIntoLevels(disposition.flatDisposition);
        const value = levels[level];
        if (value !== "") {
            values.add(value);
        }
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b));
}

function humanizeEmotoradDispositionLevelSlug(slug: string): string {
    if (slug === "") {
        return "";
    }
    return slug
        .split("-")
        .map((part) => (part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
        .join(" ");
}

/**
 * Cascading disposition options for Emotorad **log call** (and similar forms).
 * Uses the full {@link emotoradEventContainerDispositions} taxonomy — not the call-list synthetic L1
 * bucket (`connected` / `not connected` only).
 */
export function getEmotoradCallLogDispositionLevelOptions(level: "l1" | "l2" | "l3"): Array<{value: string; label: string}> {
    const slugs = getEmotoradUniqueDispositionLevelValues(level).filter((slug) => level !== "l1" || slug !== EMOTORAD_EVENT_CONTAINER_DISPOSITION_NEW_LEAD_L1_SLUG);
    return slugs.map((slug) => ({
        value: slug,
        label:
            level === "l1"
                ? (EMOTORAD_DISPOSITION_L1_LABEL_BY_FLAT_KEY[slug] ?? humanizeEmotoradDispositionLevelSlug(slug))
                : humanizeEmotoradDispositionLevelSlug(slug),
    }));
}

export function getEmotoradDispositionIdsForLevels(args: {l1Values?: Array<string>; l2Values?: Array<string>; l3Values?: Array<string>}): Array<Uuid> {
    const result: Array<Uuid> = [];
    for (const disposition of Object.values(emotoradEventContainerDispositions)) {
        const levels = splitEmotoradFlatDispositionIntoLevels(disposition.flatDisposition);
        const matchesL1 = args.l1Values === undefined || args.l1Values.includes(levels.l1);
        const matchesL2 = args.l2Values === undefined || args.l2Values.includes(levels.l2);
        const matchesL3 = args.l3Values === undefined || args.l3Values.includes(levels.l3);
        if (matchesL1 && matchesL2 && matchesL3) {
            result.push(disposition.id);
        }
    }
    return result;
}

function resolveEmotoradStatusDisplayNameById(statusId: string, statuses: Array<{id: Uuid; displayName: string}>): string | undefined {
    const normalized = statusId.trim().toLowerCase();
    if (normalized === "") {
        return undefined;
    }
    for (const status of statuses) {
        if (String(status.id).toLowerCase() === normalized) {
            return status.displayName;
        }
    }
    return undefined;
}

/** Resolves event status UUIDs for calendar/list when workspace `eventTypeLabelMap` omits Archive. */
export function formatEmotoradEventCalendarStatusIdForDisplay(statusId: string): string {
    return (
        resolveEmotoradStatusDisplayNameById(statusId, getEmotoradCallStatuses()) ??
        resolveEmotoradStatusDisplayNameById(statusId, getEmotoradSalesStatuses()) ??
        resolveEmotoradStatusDisplayNameById(statusId, getEmotoradLeadEventStatuses()) ??
        statusId
    );
}

export function formatEmotoradSalesStatusIdForDisplay(statusId: string): string {
    return resolveEmotoradStatusDisplayNameById(statusId, getEmotoradSalesStatuses()) ?? statusId;
}

/** Resolves journey lifecycle status UUIDs (Live, Converted, Archive, …) for list/table cells. */
export function formatEmotoradJourneyLifecycleStatusIdForDisplay(statusId: string): string {
    return resolveEmotoradStatusDisplayNameById(statusId, getEmotoradEventContainerStatuses()) ?? statusId;
}

const emotoradCallScheduledVisibilityRule: OttopilotAdditionalButtonVisibilityRule = {
    conditions: [
        {
            attribute: EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
            operator: OttopilotOperator.equals,
            kind: OttopilotRunTimeValueKind.static,
            value: EmotoradCallStatusEnum.SCHEDULED as string,
        },
    ],
    logicalOperator: "and",
};

const emotoradWalkinScheduledVisibilityRule: OttopilotAdditionalButtonVisibilityRule = {
    conditions: [
        {
            attribute: EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
            operator: OttopilotOperator.equals,
            kind: OttopilotRunTimeValueKind.static,
            value: EmotoradCallStatusEnum.SCHEDULED as string,
        },
    ],
    logicalOperator: "and",
};

const emotoradTestRideScheduledVisibilityRule: OttopilotAdditionalButtonVisibilityRule = {
    conditions: [
        {
            attribute: EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
            operator: OttopilotOperator.equals,
            kind: OttopilotRunTimeValueKind.static,
            value: EmotoradCallStatusEnum.SCHEDULED as string,
        },
    ],
    logicalOperator: "and",
};

/** Cancel + Log for scheduled calls — status is system-driven; cancellation remark required; log opens `/emotorad/call/log`. */
export const EMOTORAD__CALL_EVENT__ADDITIONAL_BUTTONS: Array<OttopilotAdditionalButtonsConfig> = [
    {
        id: "7a2b1c9d-4e5f-41a8-9c2b-9f1e3d4a7b21" as Uuid,
        label: "Cancel call",
        buttonKind: OttopilotAdditionalButtonKind.cancel,
        description:
            "Cancels a scheduled call. Cancellation remark is required (system auto-cancel uses Auto-Cancelled). " + "Disposition is not set when cancelled.",
        rules: [emotoradCallScheduledVisibilityRule],
        showModal: true,
        formColumnData: [
            {
                columnId: EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.user,
                sortOrder: 0,
            },
            {
                columnId: EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.timestamp,
                sortOrder: 1,
            },
            {
                columnId: EMOTORAD__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
                columnType: "OttopilotEvent",
                fieldRole: OttopilotAdditionalButtonFormFieldRole.cancelRemarks,
                isVisible: true,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                presentation: OttopilotAdditionalButtonFormFieldPresentation.multiLineText,
                sortOrder: 2,
            },
            {
                columnId: EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                defaultValue: EmotoradCallStatusEnum.CANCELLED as string,
                sortOrder: 3,
            },
            {
                columnId: EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: false,
                kind: OttopilotRunTimeValueKind.static,
                defaultValue: null,
                sortOrder: 4,
            },
        ],
    },
    {
        id: "6b1a0c8e-3d4f-40b7-8b1a-8e0d2c3b6a10" as Uuid,
        label: "Log call",
        buttonKind: OttopilotAdditionalButtonKind.log,
        description: "Complete the scheduled call as logged (disposition required on log). Default time uses current time.",
        rules: [emotoradCallScheduledVisibilityRule],
        formUrl: "emotorad/call/log",
        formUrlQueryParams: ["eventContainerId", "eventId"],
    },
];

/** Cancel + Log for scheduled walk-ins — disposition required when logged; cancellation remark required when cancelled. */
export const EMOTORAD__WALKIN_EVENT__ADDITIONAL_BUTTONS: Array<OttopilotAdditionalButtonsConfig> = [
    {
        id: "5c9b8e7d-2c3e-4f6a-7a0b-7d9c1b2a3f09" as Uuid,
        label: "Cancel walk-in",
        buttonKind: OttopilotAdditionalButtonKind.cancel,
        description: "Cancels a scheduled walk-in. Cancellation remark is required (system auto-cancel uses Auto-Cancelled).",
        rules: [emotoradWalkinScheduledVisibilityRule],
        showModal: true,
        formColumnData: [
            {
                columnId: EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.user,
                sortOrder: 0,
            },
            {
                columnId: EMOTORAD__EVENT__WALKINS__CANCELLED_ON__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.timestamp,
                sortOrder: 1,
            },
            {
                columnId: EMOTORAD__EVENT__WALKINS__CANCELLATION_REMARKS__COLUMN_ID,
                columnType: "OttopilotEvent",
                fieldRole: OttopilotAdditionalButtonFormFieldRole.cancelRemarks,
                isVisible: true,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                presentation: OttopilotAdditionalButtonFormFieldPresentation.multiLineText,
                sortOrder: 2,
            },
            {
                columnId: EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                defaultValue: EmotoradCallStatusEnum.CANCELLED as string,
                sortOrder: 3,
            },
        ],
    },
    {
        id: "4b8a7d6c-1b2d-3e5a-6f9a-6c8b0a1b2e08" as Uuid,
        label: "Log walk-in",
        buttonKind: OttopilotAdditionalButtonKind.log,
        description: "Complete the scheduled walk-in as logged. Walk-in date and time are captured when scheduling.",
        rules: [emotoradWalkinScheduledVisibilityRule],
        formUrl: "emotorad/walkin/log",
        formUrlQueryParams: ["eventContainerId", "eventId"],
    },
];

/** Cancel + Log for scheduled test rides — test ride date and time are mandatory when scheduling. */
export const EMOTORAD__TESTRIDE_EVENT__ADDITIONAL_BUTTONS: Array<OttopilotAdditionalButtonsConfig> = [
    {
        id: "3a796c5b-0a1c-2d4a-5f8a-5b7a09f0a1d07" as Uuid,
        label: "Cancel test ride",
        buttonKind: OttopilotAdditionalButtonKind.cancel,
        description: "Cancels a scheduled test ride. Cancellation remark is required (system auto-cancel uses Auto-Cancelled).",
        rules: [emotoradTestRideScheduledVisibilityRule],
        showModal: true,
        formColumnData: [
            {
                columnId: EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.user,
                sortOrder: 0,
            },
            {
                columnId: EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.timestamp,
                sortOrder: 1,
            },
            {
                columnId: EMOTORAD__EVENT__TESTRIDES__CANCELLATION_REMARKS__COLUMN_ID,
                columnType: "OttopilotEvent",
                fieldRole: OttopilotAdditionalButtonFormFieldRole.cancelRemarks,
                isVisible: true,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                presentation: OttopilotAdditionalButtonFormFieldPresentation.multiLineText,
                sortOrder: 2,
            },
            {
                columnId: EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                defaultValue: EmotoradCallStatusEnum.CANCELLED as string,
                sortOrder: 3,
            },
        ],
    },
    {
        id: "2985b4a9-f90b-1c39-4e7a-4a6908e9f0c06" as Uuid,
        label: "Log test ride",
        buttonKind: OttopilotAdditionalButtonKind.log,
        description: "Complete the scheduled test ride as logged. Default time uses current time when logging.",
        rules: [emotoradTestRideScheduledVisibilityRule],
        formUrl: "emotorad/testride/log",
        formUrlQueryParams: ["eventContainerId", "eventId"],
    },
];

/**
 * Active / inactive journey lifecycle status UUID groups for the events table "Display by" journey scope filter.
 * Returns null when the workspace is not Emotorad — the control is not rendered.
 */
export function getEmotoradJourneyScopeFilterStatusIdsForEvents(workspace: {displayName: string}): {
    activeStatusIds: Array<Uuid>;
    inactiveStatusIds: Array<Uuid>;
} | null {
    if (isEmotoradWorkspace(workspace) !== true) {
        return null;
    }
    return {
        activeStatusIds: [EmotoradEventContainerStatusEnum.LIVE as Uuid],
        inactiveStatusIds: [EmotoradEventContainerStatusEnum.CONVERTED as Uuid, EmotoradEventContainerStatusEnum.CHURNED as Uuid],
    };
}

/** Scope filter option ids for Emotorad journeys list (Assigned to me / Created by me). */

/** Journeys list scope filter: lifecycle owner column + `createdBy` (no journey status scope). */
export function getEmotoradJourneysPageScopeFilterConfig(leadOwnerColumnId: Uuid): OttopilotDataViewerConfig["scopeFilter"] {
    return {
        drawerTitle: "Type",
        selectionMode: "multi",
        options: [
            {
                id: EMOTORAD__EVENT_CONTAINERS__SCOPE_FILTER__ASSIGNED_TO_ME__OPTION_ID,
                filterKey: String(leadOwnerColumnId),
                label: "Assigned to me",
                type: "User",
                filterType: "equals",
            },
            {
                id: EMOTORAD__EVENT_CONTAINERS__SCOPE_FILTER__CREATED_BY_ME__OPTION_ID,
                filterKey: "createdBy",
                label: "Created by me",
                type: "User",
                filterType: "equals",
            },
        ],
    };
}

export function getEmotoradJourneysPageOttopilotDataViewerConfig(props: {searchPlaceholder: string; leadOwnerColumnId: Uuid}): OttopilotDataViewerConfig {
    return {
        searchColumns: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID, EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
        searchPlaceholder: props.searchPlaceholder,
        scopeFilter: getEmotoradJourneysPageScopeFilterConfig(props.leadOwnerColumnId),
    };
}

export const EMOTORAD_DEFAULT_EVENT_CONTAINER_TRIGGER_CONFIG: OttopilotTriggerConfig = [
    {
        type: OttopilotTrigger.dateTimeTrigger,
        eventTemplateId: EmotoradEventTypes.call as Uuid,
        triggerDispositions: getEmotoradEventContainerDispositionIdsRequiringScheduleNextCall(),
        dataColumns: [
            {
                columnId: EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: EmotoradCallStatusEnum.SCHEDULED,
            },
            {
                columnId: EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.runTimeDefault,
                referenceField: EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
                referenceFieldType: "OttopilotEventContainer",
            },
            {
                columnId: EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUser,
            },
            {
                columnId: EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: null,
            },
            {
                columnId: EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: EmotoradEventTypes.call as Uuid,
            },
            {
                columnId: EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.currentTimestamp,
            },
        ],
        dateTimeColumnId: EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
        scheduleFieldLabel: "Schedule call",
        scheduleValueEncoding: OttopilotScheduleValueEncoding.iso8601Instant,
    },
];

/** Option rules (hide New Lead after first user disposition), suggested churn — aligned with {@link EMOTORAD_EVENT_CONTAINER_DISPOSITION_RULES}. */
export const emotoradDispositionConfig: OttopilotDispositionConfig = {
    emitWhen: OttopilotDispositionEmitWhen.pathMatchesKnownDisposition,
    plugins: [
        {
            kind: OttopilotDispositionPluginKind.dispositionHistory,
            lastN: 20,
        },
        {
            kind: OttopilotDispositionPluginKind.optionRules,
            rules: [
                {
                    id: "850a5ed0-5c08-4f4d-8ead-df55d7c92d54" as Uuid,
                    when: {
                        level: 0,
                        selectedPrefix: [],
                    },
                    targetSegment: "new-lead",
                    countDispositionId: emotoradEventContainerDispositions[EmotoradEventContainerDispositionEnum.NEW_LEAD].id,
                    countMode: OttopilotDispositionOptionRuleCountMode.totalInHistory,
                    action: {
                        kind: OttopilotDispositionOptionRuleActionKind.hideWhenHistoryContainsNonAllowedDispositionId,
                        allowedDispositionIds: [emotoradEventContainerDispositions[EmotoradEventContainerDispositionEnum.NEW_LEAD].id],
                    },
                },
            ],
        },
        {
            kind: OttopilotDispositionPluginKind.suggestedJourneyStatusRules,
            dispositionStatusColumnId: EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
            rules: [
                {
                    kind: OttopilotSuggestedJourneyStatusRuleKind.dispositionIdInSet,
                    dispositionIds: [
                        emotoradEventContainerDispositions[EmotoradEventContainerDispositionEnum.CONNECTED_LOST_BUDGET_ISSUES].id,
                        emotoradEventContainerDispositions[EmotoradEventContainerDispositionEnum.CONNECTED_LOST_OTHER_BRAND].id,
                        emotoradEventContainerDispositions[EmotoradEventContainerDispositionEnum.CONNECTED_LOST_JUST_EXPLORING].id,
                        emotoradEventContainerDispositions[EmotoradEventContainerDispositionEnum.CONNECTED_LOST_OTHERS].id,
                    ],
                    suggested: emotoradEventContainerStatuses[EmotoradEventContainerStatusEnum.CHURNED].id,
                },
            ],
        },
    ],
};

/**
 * Leads table view — columns with Table View = TRUE in emotorad_requirement.txt (excludes Fbclid/Gclid).
 * System columns like disposition are appended in the journeys UI.
 * Aligns with journey column spec block above (incl. Created / Modified audit fields).
 */
export const EMOTORAD_JOURNEY_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SALES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    "createdAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
];

export const EMOTORAD_JOURNEY_INITIAL_FILTER_COLUMN_ID = EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID;

/** One default filter chip: try {@link fieldIds} on workspace config, then {@link nameHints} on column labels. */
export type EmotoradLeadsDefaultFilterSlot = {
    fieldIds: ReadonlyArray<string>;
    nameHints: ReadonlyArray<string>;
};

/**
 * Default filter chips for Emotorad **Leads** (lifecycle overview) and **In Queue** (lifecycle owner assignment).
 * Resolved at runtime against `tableViewColumns` + `additionalColumns` so wrapper ids and renames still match.
 */
export const EMOTORAD_LEADS_DEFAULT_VISIBLE_FILTER_SLOTS: ReadonlyArray<EmotoradLeadsDefaultFilterSlot> = [
    {fieldIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID], nameHints: ["customer name", "name"]},
    {fieldIds: [EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID], nameHints: ["contact number", "phone", "mobile"]},
    {
        fieldIds: [
            EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
            EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
            EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
            EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
            "disposition",
            "dispositionId",
        ],
        nameHints: ["status", "application status", "l3 disposition", "l2 disposition", "l1 disposition", "disposition"],
    },
    {fieldIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID], nameHints: ["customer pincode", "pincode", "pin code"]},
    {fieldIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID], nameHints: ["customer city", "city"]},
    {fieldIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID], nameHints: ["customer state", "state"]},
    {
        fieldIds: [EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID],
        nameHints: ["dealer name", "assigned dealer", "dealership", "dealer"],
    },
    {
        fieldIds: [EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID],
        nameHints: ["action owner", "lifecycle owner", "lead owner", "lifecycle owner name", "sse", "store sales"],
    },
    {fieldIds: [EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID], nameHints: ["model", "vehicle model"]},
];

/** First configured field id per slot (fallback when not resolving against workspace). */
export const EMOTORAD_LEADS_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = EMOTORAD_LEADS_DEFAULT_VISIBLE_FILTER_SLOTS.map((slot) => slot.fieldIds[0]!);

/**
 * InQueue table view (Emotorad) — explicit required columns in requested order.
 * Uses system audit fields for Modified/Created metadata.
 */
export const EMOTORAD_INQUEUE_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    EMOTORAD__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    "createdAt",
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
];

/** Lead event list — emotoradSetup.csv §3 (Table View = TRUE; omits Fbclid/Gclid). Audit uses system createdAt / createdBy / updatedAt / updatedBy, not duplicate additional columns. */
export const EMOTORAD_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_VEHICLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_HOUSE_NO__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_LANDMARK__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__FIRST_CYCLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__OTHER_BRANDS_CONSIDERED__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    "disposition",
    "status",
    "createdAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
];

/** Calls list — emotoradSetup.csv §5 (Table View = TRUE). Lifecycle owner is on the journey only; audit uses system fields. */
export const EMOTORAD_CALL_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    "disposition",
    "createdAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
];

/** Calls list (Emotorad) — explicit required columns in requested order, with synthetic L1/L2/L3. */
export const EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
];

/** Walk-ins list (Emotorad) — explicit required columns in requested order, with synthetic L1/L2/L3. */
export const EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LAST_WALKIN_DATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    EMOTORAD__EVENT__WALKINS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__UTM_CONTENT__COLUMN_ID,
];

/**
 * Leads list (Emotorad) — filter bar chips shown by default on Table view.
 * Matches product default: name, contact, status, geo, dealer, owner, model.
 */
export const EMOTORAD_LEADS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    "createdAt",
    "updatedBy",
    "updatedAt",
];

/** Calls list — default filter chips (intersected with {@link EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS}). */
export const EMOTORAD_CALLS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    "updatedBy",
    "updatedAt",
];

/** Walk-ins list — default filter chips (intersected with {@link EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS}). */
export const EMOTORAD_WALKINS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    "updatedBy",
    "updatedAt",
];

/** Test ride list — default filter chips (intersected with {@link EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS}). */
export const EMOTORAD_TESTRIDE_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    "updatedBy",
    "updatedAt",
];

/** Sales list — default filter chips (intersected with {@link EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS}). */
export const EMOTORAD_SALES_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    "createdAt",
    "updatedBy",
    "updatedAt",
];

/**
 * Keeps `preferred` order; returns only ids that appear in `moduleColumnIds` (deduped).
 */
export function intersectEmotoradPreferredVisibleFilterColumnIdsWithModule(
    preferred: ReadonlyArray<string>,
    moduleColumnIds: ReadonlyArray<string>,
): Array<string> {
    const allowed = new Set(moduleColumnIds);
    const seen = new Set<string>();
    const out: Array<string> = [];
    for (const id of preferred) {
        if (allowed.has(id) !== true || seen.has(id) === true) {
            continue;
        }
        seen.add(id);
        out.push(id);
    }
    return out;
}

/** Leads list (Emotorad) — explicit required columns in requested order, with synthetic L1/L2/L3. */
export const EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_VEHICLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_HOUSE_NO__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_LANDMARK__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__FIRST_CYCLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__OTHER_BRANDS_CONSIDERED__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
    "createdAt",
    EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    EMOTORAD__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
];

export const EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__FRAME_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    "createdAt",
    "updatedBy",
    "updatedAt",
    EMOTORAD__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__KAM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__RM__COLUMN_ID,
];

/** Test ride list (Emotorad) — explicit required columns in requested order. */
export const EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLATION_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    EMOTORAD__EVENT__TESTRIDES__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__UTM_CONTENT__COLUMN_ID,
];

/** Disposition Tracker list (Emotorad) — explicit required columns in requested order, with synthetic L1/L2/L3. */
export const EMOTORAD_DISPOSITION_TRACKER_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__DISPOSITION_TRACKER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__DISPOSITION_TRACKER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__DISPOSITION_TRACKER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    "disposition",
    "createdAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
];

export const emotoradJourneyAonConfig: AonWorkspaceConfig = {
    displayColumnId: String(EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID),
    rangesColumnId: "__emotorad_aon_ranges__",
    proxyColumnId: "__emotorad_aon_filter__",
    createdAtPickerColumnId: "createdAt",
    createdAtApiColumn: "createdAt",
};

export const emotoradEnquiryAonConfig: AonWorkspaceConfig = {
    displayColumnId: String(EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID),
    rangesColumnId: "__emotorad_aon_ranges__",
    proxyColumnId: "__emotorad_aon_filter__",
    createdAtPickerColumnId: "createdAt",
    createdAtApiColumn: "createdAt",
};

/** Legacy CC maps sub-zones to a single South bucket (see emotrad dealers/filters SQL). */
export function normalizeEmotoradCustomerZone(zone: string): string {
    const trimmed = zone.trim();
    if (trimmed === "South 1" || trimmed === "South 2") {
        return "South";
    }
    return trimmed;
}

/** Expands normalized filter selections so API queries match stored sub-zone values. */
export function expandEmotoradCustomerZoneFilterValues(zones: ReadonlyArray<string>): Array<string> {
    const expanded = new Set<string>();
    for (const zone of zones) {
        const trimmed = zone.trim();
        if (trimmed === "") {
            continue;
        }
        const normalized = normalizeEmotoradCustomerZone(trimmed);
        if (normalized === "South") {
            expanded.add("South");
            expanded.add("South 1");
            expanded.add("South 2");
            continue;
        }
        expanded.add(normalized);
        if (normalized !== trimmed) {
            expanded.add(trimmed);
        }
    }
    return Array.from(expanded);
}

export function splitEmotoradFlatDispositionIntoLevels(flatDisposition: string): {l1: string; l2: string; l3: string} {
    const parts = flatDisposition.split("_");
    const l1 = parts[0] ?? "";
    const l2 = parts[1] ?? "";
    const l3 = parts.length <= 2 ? "" : parts.slice(2).join("_");
    return {l1: l1, l2: l2, l3: l3};
}

/**
 * Call events: L1 column is only "connected" or "not connected", derived from {@link emotoradEventContainerDispositions} flat slug
 * (e.g. interested_* / not-interested_* / connected_* → connected; not-connected_* / new-lead → not connected).
 */
function emotoradCallSyntheticL1LabelFromFlatDisposition(flatDisposition: string): string {
    if (flatDisposition.trim() === "") {
        return "";
    }
    const l1Segment = splitEmotoradFlatDispositionIntoLevels(flatDisposition).l1;
    if (l1Segment === "not-connected" || l1Segment === "new-lead") {
        return "not connected";
    }
    if (l1Segment === "connected" || l1Segment === "interested" || l1Segment === "not-interested") {
        return "connected";
    }
    return "";
}

function emotoradFlatDispositionForDispositionId(dispositionId: Uuid): string | undefined {
    for (const d of Object.values(emotoradEventContainerDispositions)) {
        if (d.id === dispositionId) {
            return d.flatDisposition;
        }
    }
    return undefined;
}

function normalizeEmotoradAdditionalColumnUuidString(value: unknown): string | undefined {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (typeof value === "string" && value.trim() !== "") {
        return value;
    }
    return undefined;
}

/** Last logged call's journey-style disposition on this journey (`CALL_DISPOSITION` column). */
export function resolveEmotoradLastLoggedCallDispositionId(journey: EventContainerWithEvents): Uuid | undefined {
    const callTemplateId = EmotoradEventTypes.call as Uuid;
    const journeyEvents = Array.isArray(journey.events) ? journey.events : [];
    const loggedCalls = journeyEvents
        .filter((journeyEvent) => {
            if (journeyEvent.templateId !== callTemplateId) {
                return false;
            }
            return String(journeyEvent.additionalColumnValues[EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID]) === String(EmotoradCallStatusEnum.LOGGED);
        })
        .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt));
    for (const journeyEvent of loggedCalls) {
        const fromColumn = normalizeEmotoradAdditionalColumnUuidString(
            journeyEvent.additionalColumnValues[EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID],
        );
        if (fromColumn !== undefined) {
            return fromColumn as Uuid;
        }
    }
    return undefined;
}

/**
 * Journey-style disposition from {@link EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID}.
 * Logged calls: use the column when set.
 * Scheduled calls: only when the column matches the last logged call disposition (set on schedule after a log).
 */
function resolveEmotoradCallEventCallDispositionId(event: EventWithEventContainer): Uuid | undefined {
    const fromColumn = normalizeEmotoradAdditionalColumnUuidString(event.additionalColumnValues[EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID]);
    if (fromColumn === undefined) {
        return undefined;
    }
    const flat = emotoradFlatDispositionForDispositionId(fromColumn as Uuid);
    if (flat === undefined) {
        return undefined;
    }
    const status = String(event.additionalColumnValues[EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID]);
    if (status === String(EmotoradCallStatusEnum.LOGGED)) {
        return fromColumn as Uuid;
    }
    if (status === String(EmotoradCallStatusEnum.SCHEDULED)) {
        if (splitEmotoradFlatDispositionIntoLevels(flat).l1 === "new-lead") {
            return undefined;
        }
        const journeyEvents = (event.eventContainer as EventContainerWithEvents).events;
        if (Array.isArray(journeyEvents) === true) {
            const lastLoggedDispositionId = resolveEmotoradLastLoggedCallDispositionId({
                ...event.eventContainer,
                events: journeyEvents,
            } as EventContainerWithEvents);
            if (lastLoggedDispositionId === undefined) {
                return undefined;
            }
            if (fromColumn !== lastLoggedDispositionId) {
                return undefined;
            }
        }
        return fromColumn as Uuid;
    }
    return undefined;
}

/** Template id → disposition column id for journey-style (L1/L2/L3) dispositions stored on the event row. */
const EMOTORAD_EVENT_TEMPLATE_ID_TO_JOURNEY_STYLE_DISPOSITION_COLUMN_ID: Partial<Record<string, Uuid>> = {
    [String(EmotoradEventTypes.walkin)]: EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID,
    [String(EmotoradEventTypes.call)]: EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
    [String(EmotoradEventTypes.testride)]: EMOTORAD__EVENT__TESTRIDES__DISPOSITION__COLUMN_ID,
};

const EMOTORAD_EVENT_ALL_JOURNEY_STYLE_DISPOSITION_COLUMN_IDS: Array<Uuid> = [
    EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__DISPOSITION__COLUMN_ID,
];

/**
 * Resolves which UUID represents the journey-style disposition for synthetic L1/L2/L3.
 * Sources differ by template and payload shape: configured Event/Container
 * disposition columns or other known disposition columns on the event.
 *
 * **Call events:** only {@link EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID} (last call disposition).
 * Do not fall back to the Event template outcome (Logged/Scheduled/Cancelled) or journey disposition —
 * that incorrectly surfaced e.g. journey `new-lead` as call L1 "not connected" on scheduled calls.
 */
function resolveEmotoradEventDispositionIdForSyntheticColumns(
    event: EventWithEventContainer,
    eventTemplate: OttopilotEventTemplate,
    eventContainerTemplate: EventContainerTemplate,
): Uuid {
    const eventDispositionId = getEventDispositionId(event, eventTemplate);
    const journeyDispositionId = getEventContainerDispositionId(event.eventContainer, eventContainerTemplate);
    const templateKey = event.templateId as string;
    if (templateKey === String(EmotoradEventTypes.call)) {
        const callDispositionId = resolveEmotoradCallEventCallDispositionId(event);
        if (callDispositionId !== undefined) {
            return callDispositionId;
        }
        return eventDispositionId;
    }
    const preferredColumnId = EMOTORAD_EVENT_TEMPLATE_ID_TO_JOURNEY_STYLE_DISPOSITION_COLUMN_ID[templateKey];

    const candidateIdStrings: Array<string> = [];
    const pushCandidate = (value: string | undefined) => {
        if (value === undefined) {
            return;
        }
        const trimmed = value.trim();
        if (trimmed === "") {
            return;
        }
        if (candidateIdStrings.includes(trimmed) !== true) {
            candidateIdStrings.push(trimmed);
        }
    };

    if (preferredColumnId !== undefined) {
        pushCandidate(normalizeEmotoradAdditionalColumnUuidString(event.additionalColumnValues[preferredColumnId]));
    }
    pushCandidate(String(eventDispositionId));
    pushCandidate(String(journeyDispositionId));
    for (const columnId of EMOTORAD_EVENT_ALL_JOURNEY_STYLE_DISPOSITION_COLUMN_IDS) {
        if (preferredColumnId !== undefined && columnId === preferredColumnId) {
            continue;
        }
        pushCandidate(normalizeEmotoradAdditionalColumnUuidString(event.additionalColumnValues[columnId]));
    }

    for (const idString of candidateIdStrings) {
        const asUuid = idString as Uuid;
        if (emotoradFlatDispositionForDispositionId(asUuid) !== undefined) {
            return asUuid;
        }
    }
    if (candidateIdStrings.length > 0) {
        return candidateIdStrings[0]! as Uuid;
    }
    return eventDispositionId;
}

/**
 * Journey-style leaf disposition UUID for this event (same resolution as synthetic L1–L3).
 * Use for disposition-tracker and other read paths when the platform stores disposition across column / `dispositionId`.
 */
export function resolveEmotoradEventJourneyStyleDispositionId(
    event: EventWithEventContainer,
    eventTemplate: OttopilotEventTemplate,
    eventContainerTemplate: EventContainerTemplate,
): Uuid {
    return resolveEmotoradEventDispositionIdForSyntheticColumns(event, eventTemplate, eventContainerTemplate);
}

export function buildEmotoradEventSyntheticColumnValues(
    event: EventWithEventContainer,
    eventTemplate: OttopilotEventTemplate,
    eventContainerTemplate: EventContainerTemplate,
): Record<Uuid, string> {
    const templateIdString = event.templateId as string;
    const isWalkin = templateIdString === String(EmotoradEventTypes.walkin);
    const isCall = templateIdString === String(EmotoradEventTypes.call);
    const isDispositionTracker = templateIdString === String(EmotoradEventTypes.dispositionTracker);
    const isLead = templateIdString === String(EmotoradEventTypes.lead);
    const isSales = templateIdString === String(EmotoradEventTypes.sales);
    const isTestride = templateIdString === String(EmotoradEventTypes.testride);

    if (isWalkin !== true && isCall !== true && isDispositionTracker !== true && isLead !== true && isSales !== true && isTestride !== true) {
        return {};
    }
    if (isCall === true && String(event.additionalColumnValues[EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID]) === String(EmotoradCallStatusEnum.CANCELLED)) {
        return {
            [EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID]: "",
            [EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID]: "",
            [EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID]: "",
        };
    }
    //Todo (Jugaad)
    if (
        isWalkin === true &&
        [String(EmotoradCallStatusEnum.SCHEDULED), String(EmotoradCallStatusEnum.CANCELLED)].includes(
            String(event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID]),
        )
    ) {
        return {
            [EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID]: "",
            [EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID]: "",
            [EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID]: "",
        };
    }
    const callDispositionId = isCall === true ? resolveEmotoradCallEventCallDispositionId(event) : undefined;
    const dispositionId = isCall === true ? callDispositionId : resolveEmotoradEventDispositionIdForSyntheticColumns(event, eventTemplate, eventContainerTemplate);
    const flat = dispositionId === undefined ? undefined : emotoradFlatDispositionForDispositionId(dispositionId);
    const levels = flat === undefined ? {l1: "", l2: "", l3: ""} : splitEmotoradFlatDispositionIntoLevels(flat);
    if (isWalkin === true) {
        return {
            [EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID]: levels.l1,
            [EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID]: levels.l2,
            [EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID]: levels.l3,
        };
    }
    if (isDispositionTracker === true) {
        return {
            [EMOTORAD__EVENT__DISPOSITION_TRACKER__L1_DISPOSITION__COLUMN_ID]: levels.l1,
            [EMOTORAD__EVENT__DISPOSITION_TRACKER__L2_DISPOSITION__COLUMN_ID]: levels.l2,
            [EMOTORAD__EVENT__DISPOSITION_TRACKER__L3_DISPOSITION__COLUMN_ID]: levels.l3,
        };
    }
    if (isTestride === true) {
        return {
            [EMOTORAD__EVENT__TESTRIDES__L1_DISPOSITION__COLUMN_ID]: levels.l1,
            [EMOTORAD__EVENT__TESTRIDES__L2_DISPOSITION__COLUMN_ID]: levels.l2,
            [EMOTORAD__EVENT__TESTRIDES__L3_DISPOSITION__COLUMN_ID]: levels.l3,
        };
    }
    if (isCall !== true) {
        return {};
    }
    const callL1 = flat !== undefined ? emotoradCallSyntheticL1LabelFromFlatDisposition(flat) : levels.l1;
    return {
        [EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID]: callL1,
        [EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID]: levels.l2,
        [EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID]: levels.l3,
    };
}

/**
 * Derives synthetic L1/L2/L3 display strings for call, walk-in, test ride, or disposition tracker events from the flat disposition slug
 * (underscore-separated levels, see {@link emotoradEventContainerDispositions}) and merges them into `additionalColumnValues`.
 * For **call** events only, L1 is normalized to `"connected"` or `"not connected"` from that slug; L2/L3 stay the raw slug levels.
 */
export function emotoradEventSyntheticFunction(
    event: EventWithEventContainer,
    eventTemplate: OttopilotEventTemplate,
    eventContainerTemplate: EventContainerTemplate,
): EventWithEventContainer {
    const synthetic = buildEmotoradEventSyntheticColumnValues(event, eventTemplate, eventContainerTemplate);
    const journeySynthetic = buildEmotoradJourneySyntheticColumnValues({
        disposition: getEventContainerDispositionId(event.eventContainer, eventContainerTemplate),
        createdAt: event.eventContainer.createdAt,
    });
    const isScheduledCall =
        String(event.templateId) === String(EmotoradEventTypes.call) &&
        String(event.additionalColumnValues[EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID]) === String(EmotoradCallStatusEnum.SCHEDULED);
    const showCallDispositionColumn = isScheduledCall !== true || resolveEmotoradCallEventCallDispositionId(event) !== undefined;
    return {
        ...event,
        additionalColumnValues: {
            ...event.additionalColumnValues,
            ...synthetic,
            ...(showCallDispositionColumn === false ? {[EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID]: ""} : {}),
        },
        eventContainer: {
            ...event.eventContainer,
            additionalColumnValues: {
                ...event.eventContainer.additionalColumnValues,
                ...journeySynthetic,
            },
        },
    };
}

export function buildEmotoradJourneySyntheticColumnValues(source: {
    disposition: Uuid | null | undefined;
    createdAt: Iso8601DateTime;
}): Record<Uuid, string> {
    const flat = source.disposition === null || source.disposition === undefined
        ? undefined
        : emotoradFlatDispositionForDispositionId(source.disposition);
    const levels = flat === undefined ? {l1: "", l2: "", l3: ""} : splitEmotoradFlatDispositionIntoLevels(flat);
    const aonDays = computeAonDaysFromCreatedAtIso(source.createdAt);
    const aon = aonDays === null ? "" : String(aonDays);
    return {
        [EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]: levels.l1,
        [EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID]: levels.l2,
        [EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID]: levels.l3,
        [EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID as Uuid]: aon,
    };
}

/**
 * Synthetic journey columns: L1/L2/L3 from the configured disposition UDO value; AON = whole days from `createdAt` to now (non-negative).
 * Merged into `additionalColumnValues`.
 */
export function emotoradJourneySyntheticFunction(
    journey: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplate,
): EventContainerWithEvents {
    const synthetic = buildEmotoradJourneySyntheticColumnValues({
        disposition: getEventContainerDispositionId(journey, eventContainerTemplate),
        createdAt: journey.createdAt,
    });
    return {
        ...journey,
        additionalColumnValues: {
            ...journey.additionalColumnValues,
            ...synthetic,
        },
    };
}
